/**
 * Проверяет, может ли пользователь редактировать задачу
 * @param {number} taskCreatorId - ID создателя задачи
 * @param {number} currentUserId - ID текущего пользователя
 * @param {string} userRole - Роль текущего пользователя
 * @param {Object} task - Объект задачи (опционально, для дополнительных проверок)
 * @returns {Object} - { canEdit: boolean, reason: string }
 */
export const canEditTask = (taskCreatorId, currentUserId, userRole, task = null) => {
    // Суперадмин и админ могут редактировать любые задачи
    if (userRole === 'superadmin' || userRole === 'admin') {
        return { canEdit: true, reason: '' };
    }

    // HR может редактировать любые задачи
    if (userRole === 'hr') {
        return { canEdit: true, reason: '' };
    }

    // Leader может редактировать только если он:
    // 1. Создатель задачи, ИЛИ
    // 2. Исполнитель/соисполнитель задачи
    if (userRole === 'leader') {
        // Проверяем является ли создателем
        if (taskCreatorId === currentUserId) {
            return { canEdit: true, reason: '' };
        }

        // Проверяем является ли исполнителем/соисполнителем
        if (task && (currentUserId === task.assignee_id || currentUserId === task.co_assignee_id)) {
            return { canEdit: true, reason: '' };
        }

        return {
            canEdit: false,
            reason: 'Вы можете редактировать только свои задачи или задачи, где вы исполнитель/соисполнитель'
        };
    }

    // Все остальные роли не могут редактировать задачи
    return {
        canEdit: false,
        reason: 'У вас нет прав для редактирования задач'
    };
};

/**
 * Проверяет, может ли пользователь управлять задачей (начать/завершить)
 * @param {Object} task - Объект задачи
 * @param {number} currentUserId - ID текущего пользователя
 * @param {string} userRole - Роль текущего пользователя
 * @returns {boolean} - true если может управлять
 */
export const canManageTaskStatus = (task, currentUserId, userRole) => {
    if (!task) return false;

    // hr, admin, superadmin могут всегда управлять задачами
    if (userRole === 'superadmin' || userRole === 'admin' || userRole === 'hr') {
        return true;
    }

    // employee может управлять только своими задачами (где он участник)
    if (userRole === 'employee') {
        return currentUserId === task.creator_id ||
            currentUserId === task.assignee_id ||
            currentUserId === task.co_assignee_id ||
            currentUserId === task.observer_id;
    }

    // leader может управлять только если он исполнитель или соисполнитель
    if (userRole === 'leader') {
        return currentUserId === task.assignee_id || currentUserId === task.co_assignee_id;
    }

    return false;
};

/**
 * Проверяет, может ли пользователь просматривать задачу
 * @param {Object} task - Объект задачи
 * @param {number} currentUserId - ID текущего пользователя
 * @param {string} userRole - Роль текущего пользователя
 * @returns {Object} - { canView: boolean, reason: string }
 */
export const canViewTask = (task, currentUserId, userRole) => {
    if (!task) {
        return { canView: false, reason: 'Задача не найдена' };
    }

    // Суперадмин, админ и HR могут просматривать любые задачи
    if (userRole === 'superadmin' || userRole === 'admin' || userRole === 'hr') {
        return { canView: true, reason: '' };
    }

    // Employee может просматривать только задачи, где он участник
    if (userRole === 'employee') {
        const isParticipant =
            currentUserId === task.creator_id ||
            currentUserId === task.assignee_id ||
            currentUserId === task.co_assignee_id ||
            currentUserId === task.observer_id;

        if (isParticipant) {
            return { canView: true, reason: '' };
        }

        return {
            canView: false,
            reason: 'Вы можете просматривать только задачи, в которых вы являетесь участником'
        };
    }

    // Leader может просматривать задачи, где он участник
    if (userRole === 'leader') {
        const isParticipant =
            currentUserId === task.creator_id ||
            currentUserId === task.assignee_id ||
            currentUserId === task.co_assignee_id ||
            currentUserId === task.observer_id;

        if (isParticipant) {
            return { canView: true, reason: '' };
        }

        return {
            canView: false,
            reason: 'Вы можете просматривать только задачи, в которых вы являетесь участником'
        };
    }

    // Все остальные роли не могут просматривать задачи
    return {
        canView: false,
        reason: 'У вас нет прав для просмотра задач'
    };
};

